/**
 * hlsController — attaches an HLS source to a <video> element with an aggressive
 * forward buffer so the next ~5 minutes are pre-loaded (the "YouTube feel" the
 * product requires). ABR is left on auto: hls.js measures throughput per segment
 * and drops to the low rung on bad internet, climbing back when it recovers.
 *
 * Safari plays HLS natively; every other browser uses hls.js (MSE).
 */
import Hls from "hls.js";

export interface QualityLevel {
  index: number; // -1 = auto
  height: number;
  bitrate: number;
}

export interface HlsHandle {
  destroy(): void;
  /** -1 = auto ABR, otherwise a specific level index. */
  setLevel(index: number): void;
  getLevels(): QualityLevel[];
  onLevelSwitch(cb: (height: number, auto: boolean) => void): void;
  isNative: boolean;
}

/** Five minutes of look-ahead. hls.js caps buffering by this many seconds. */
export const FORWARD_BUFFER_S = 300;
/** Keep a little behind-buffer so small seeks back are instant. */
export const BACK_BUFFER_S = 30;

export function attachHls(video: HTMLVideoElement, src: string): HlsHandle {
  // Native HLS (Safari / iOS): the browser manages buffering + ABR itself.
  if (!Hls.isSupported() && video.canPlayType("application/vnd.apple.mpegurl")) {
    video.src = src;
    return {
      destroy() {
        video.removeAttribute("src");
        video.load();
      },
      setLevel() {
        /* native ABR only */
      },
      getLevels() {
        return [];
      },
      onLevelSwitch() {
        /* not exposed by native */
      },
      isNative: true,
    };
  }

  const hls = new Hls({
    // Pre-buffer the next 5 minutes.
    maxBufferLength: FORWARD_BUFFER_S,
    maxMaxBufferLength: FORWARD_BUFFER_S,
    // Cap by size too so 1080p doesn't blow memory (bytes).
    maxBufferSize: 600 * 1000 * 1000,
    backBufferLength: BACK_BUFFER_S,
    // Start on auto and let the ABR controller choose from measured bandwidth.
    startLevel: -1,
    // Be quick to recover from stalls / gaps on flaky links.
    lowLatencyMode: false,
    enableWorker: true,
    fragLoadingMaxRetry: 6,
    manifestLoadingMaxRetry: 4,
    levelLoadingMaxRetry: 4,
  });

  hls.loadSource(src);
  hls.attachMedia(video);

  let levelCb: ((height: number, auto: boolean) => void) | null = null;
  hls.on(Hls.Events.LEVEL_SWITCHED, (_e, data) => {
    const lvl = hls.levels[data.level];
    if (lvl && levelCb) levelCb(lvl.height, hls.autoLevelEnabled);
  });

  // Best-effort recovery from transient network/media errors.
  hls.on(Hls.Events.ERROR, (_e, data) => {
    if (!data.fatal) return;
    if (data.type === Hls.ErrorTypes.NETWORK_ERROR) hls.startLoad();
    else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) hls.recoverMediaError();
    else hls.destroy();
  });

  return {
    destroy() {
      hls.destroy();
    },
    setLevel(index: number) {
      hls.currentLevel = index; // -1 restores auto ABR
    },
    getLevels() {
      return hls.levels.map((l, index) => ({ index, height: l.height, bitrate: l.bitrate }));
    },
    onLevelSwitch(cb) {
      levelCb = cb;
    },
    isNative: false,
  };
}
